import { type ClassValue, clsx } from "clsx";
import { extendTailwindMerge } from "tailwind-merge";
import { parse, format } from "date-fns";
import { VenueDetails } from "common/types";
import { backendUrl } from "common/config";
import { isJwtExpired } from "./jwt";

// Extend twMerge to handle custom typography classes
const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      "font-size": [
        // Custom typography classes that conflict with each other
        {
          text: [
            "9-reg",
            "11-med",
            "12-bold",
            "12-semi",
            "12-med",
            "12-med-tight",
            "12-reg",
            "14-semi",
            "14-semi-tight",
            "14-med",
            "14-med-tight",
            "14-reg",
            "16-bold",
            "16-semi",
            "16-med",
            "16-reg",
            "18-bold",
            "18-bold-tight",
            "18-semi",
            "18-semi-tight",
            "20-xbold",
            "20-xbold-tight",
            "20-bold",
            "20-semi",
            "24-bold-tight",
            "24-semi-tight",
            "26-bold",
            "26-xbold",
            "32-xbold",
          ],
        },
      ],
    },
  },
});

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatDate(input: string): string {
  try {
    if (input === "") return input; // Check if the string is empty
    // Parse the input string into a Date object
    const parsedDate = parse(input.split("")[0], "dd-MM-yyyy", new Date());

    // Format the Date object into the desired format
    const formattedDate = format(parsedDate, "dd MMMM yy");

    return formattedDate;
  } catch (error) {
    console.log(error);
    return input;
  }
}

export function formatFullDate(input: string): string {
  //input e.g. 2023-04-25T11:52:41.000000Z
  try {
    if (input === "") return input; // Check if the string is empty
    // Parse the input string into a Date object
    const formattedDate = format(new Date(input), "MMMM yyyy");
    return formattedDate;
  } catch (error) {
    console.log(error);
    return input;
  }
}

/**
 * Returns the correct slugs for navigation based on the venue's slug context.
 *
 * - "general" visibility_type: the user arrived via an alternate public slug
 *   (e.g. Guests & Visitors). All outgoing links AND back-to-venue-details
 *   use that alternate slug to keep the user in the same visibility context.
 *
 * - "walkthrough" visibility_type: the slug belongs to a cohort card shown on
 *   the venue details page. Outgoing content links use the cohort slug, but
 *   back-to-venue-details always returns to the main business slug.
 *
 * Returns:
 *   venueDetailsSlug — slug for /venuedetails/ back navigation
 *   contentSlug      — slug for /accessibility-guide/, /whats-nearby/,
 *                      /complexvenuetabs/ outgoing links
 */
export function getVenueSlugs(details: VenueDetails): {
  venueDetailsSlug: string;
  contentSlug: string;
} {
  const mainSlug = details.business.slug;
  const slugDetails = details.businessSlugDetails;

  if (!slugDetails) {
    return { venueDetailsSlug: mainSlug, contentSlug: mainSlug };
  }

  if (slugDetails.visibility_type === "general") {
    return {
      venueDetailsSlug: slugDetails.slug,
      contentSlug: slugDetails.slug,
    };
  }

  // "walkthrough" (and any unrecognised future type): content links use the
  // alternate/cohort slug, but back-to-venue-details uses the main slug.
  return {
    venueDetailsSlug: mainSlug,
    contentSlug: slugDetails.slug,
  };
}

/**
 * localStorage key used to persist a venue's password token across pages.
 * The token is scoped per-slug so different password-protected venues
 * each have their own stored token.
 */
export const passwordTokenStorageKey = (slug: string) => `pw_token_${slug}`;

/**
 * Returns the stored password token for a given slug, or undefined if none.
 * Used by all routes that call sync-spaces for password-protected venues.
 */
export function getStoredPasswordToken(slug: string): string | undefined {
  if (typeof window === "undefined") return undefined;
  return localStorage.getItem(passwordTokenStorageKey(slug)) ?? undefined;
}

/**
 * localStorage key used to persist the SSO token for a venue across pages.
 * Scoped per-slug so different SSO-protected venues have separate tokens.
 */
export const ssoTokenStorageKey = (slug: string) => `sso_token_${slug}`;

/**
 * Returns the stored SSO token for a given slug.
 * @param slug - venue slug the token is scoped to
 * @returns the stored token, or undefined when absent or expired
 */
export function getStoredSsoToken(slug: string): string | undefined {
  if (typeof window === "undefined") return undefined;
  const token = localStorage.getItem(ssoTokenStorageKey(slug));
  if (!token) return undefined;
  // An expired token still reads as "authenticated" to the SSO redirect guard,
  // stranding the venue page on "Redirecting to login..." with no way out.
  if (isJwtExpired(token)) {
    localStorage.removeItem(ssoTokenStorageKey(slug));
    return undefined;
  }
  return token;
}

/**
 * On a fresh navigation back from the SSO provider, the backend stashes
 * the issued token in `sessionStorage` under `__sso_token_incoming`.
 * This consumes that token (removing it from sessionStorage), persists
 * it to localStorage scoped to the venue slug, and returns it. Falls
 * back to a URL-supplied token, then to whatever's already stored.
 *
 * SSR-safe: returns undefined on the server.
 */
export function consumeIncomingSsoToken(
  slug: string,
  urlSsoToken: string | undefined,
): string | undefined {
  if (typeof window === "undefined") return undefined;
  const incomingToken = sessionStorage.getItem("__sso_token_incoming");
  if (incomingToken) {
    sessionStorage.removeItem("__sso_token_incoming");
    localStorage.setItem(ssoTokenStorageKey(slug), incomingToken);
    return incomingToken;
  }
  return urlSsoToken || getStoredSsoToken(slug);
}

/**
 * Builds the backend SAML2 SSO initiation URL.
 *
 * Flow:
 *   FE → GET {backendUrl}/saml2/{ssoTenantId}/login?returnTo={venueUrl}
 *   Backend → IdP (Azure AD / ADFS / SecureAuth / etc.)
 *   IdP → POST {backendUrl}/saml2/{ssoTenantId}/acs
 *   Backend → GET /api/v1/user/saml-user?returnTo={venueUrl}
 *   Backend → {venueUrl}?token={jwt}
 *   FE reads ?token= and sends as Authorization: Bearer {token}
 *
 * @param ssoTenantId - Tenant key from the sync-spaces API (maps to a SAML2 IdP in the DB)
 * @param returnSlug  - The venue slug to return to after authentication
 */
export function buildSsoRedirectUrl(
  ssoTenantId: string,
  returnSlug: string,
): string {
  const returnBase =
    (import.meta.env.VITE_SSO_RETURN_BASE_URL as string | undefined) ||
    (typeof window !== "undefined" ? window.location.origin : "");

  const frontendUrl = `${returnBase}/venuedetails/${returnSlug}`;

  const samlUserUrl = `${backendUrl}/api/v1/user/saml-user?returnTo=${encodeURIComponent(frontendUrl)}`;

  return `${backendUrl}/saml2/${ssoTenantId}/login?returnTo=${encodeURIComponent(samlUserUrl)}`;
}
